import {
  createRootRoute, createRoute, createRouter,
  RouterProvider, Outlet, redirect,
} from '@tanstack/react-router';
import { Header } from './components/Header.tsx';
import { BottomNav } from './components/BottomNav.tsx';
import { Sidebar } from './components/Sidebar.tsx';
import { TopBar } from './components/TopBar.tsx';
import { Login } from './pages/Login.tsx';
import { AcceptInvite } from './pages/AcceptInvite.tsx';
import { ForgotPassword } from './pages/ForgotPassword.tsx';
import { SsoHandoff } from './pages/SsoHandoff.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { Profile } from './pages/Profile.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { Houses } from './pages/Houses.tsx';
import { HouseRegister } from './pages/HouseRegister.tsx';
import { PersonDetail } from './pages/PersonDetail.tsx';
import { FamilyTree } from './pages/FamilyTree.tsx';
import { DhaftharuList } from './pages/DhaftharuList.tsx';
import { DhaftharuDetail } from './pages/DhaftharuDetail.tsx';
import { AdminOrgs } from './pages/AdminOrgs.tsx';
import { AdminOrgDetail } from './pages/AdminOrgDetail.tsx';
import { Settings } from './pages/Settings.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { Surveys } from './pages/Surveys.tsx';
import { SurveyBuilder } from './pages/SurveyBuilder.tsx';
import { SurveyCollect } from './pages/SurveyCollect.tsx';
import { SurveyResults } from './pages/SurveyResults.tsx';
import { SurveyResponseEdit } from './pages/SurveyResponseEdit.tsx';
import { ZoneIssues } from './pages/zone/ZoneIssues.tsx';
import { ZoneNewIssue } from './pages/zone/ZoneNewIssue.tsx';
import { ZoneIssueDetail } from './pages/zone/ZoneIssueDetail.tsx';
import { ZoneDashboard } from './pages/zone/ZoneDashboard.tsx';
import { ZoneSettings } from './pages/zone/ZoneSettings.tsx';
import { hasCensus, hasZone, isCouncillor } from './features/zone/access.ts';
import { useAuthStore } from './store/auth.ts';

function RootLayout(): JSX.Element {
  // Sidebar / BottomNav / Header internals already bail when !user, but the
  // <main> element reserves space for the sidebar with md:ms-60. When the user
  // is signed out (Login, AcceptInvite) that offset pushes content right with
  // nothing on the left — so condition the offset on auth state.
  const user = useAuthStore((s) => s.user);
  return (
    <div className={`min-h-screen flex flex-col ${user ? 'bg-[hsl(var(--chrome))]' : ''}`}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-2 focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <TopBar />
      <Sidebar />
      {/* Fixed inverted-corner masks, so the white content panel reads as rounded
          against the chrome and the corners stay put while the page scrolls (they
          are not on the scrolling content). They paint only the chrome-coloured
          notch; the content shows through the transparent part. */}
      {user && (
        <div
          aria-hidden
          className="pointer-events-none fixed left-64 top-16 z-30 hidden size-8 md:block"
          style={{ background: 'radial-gradient(circle at bottom right, transparent 31px, hsl(var(--chrome)) 32px)' }}
        />
      )}
      {user && (
        <div
          aria-hidden
          className="pointer-events-none fixed right-0 top-16 z-30 hidden size-8 md:block"
          style={{ background: 'radial-gradient(circle at bottom left, transparent 31px, hsl(var(--chrome)) 32px)' }}
        />
      )}
      <Header />
      <main
        id="main-content"
        tabIndex={-1}
        className={`flex-1 ${user ? 'has-bottom-nav bg-background md:bg-transparent md:ml-64 md:pt-16' : ''}`}
      >
        {user ? (
          <div className="min-h-dvh bg-background md:min-h-[calc(100dvh-4rem)]">
            <Outlet />
          </div>
        ) : (
          <Outlet />
        )}
      </main>
      <BottomNav />
    </div>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const requireAuth = (): void => {
  if (!useAuthStore.getState().accessToken) throw redirect({ to: '/login' });
};

const requireOrgMember = (): void => {
  const state = useAuthStore.getState();
  if (!state.accessToken) throw redirect({ to: '/login' });
  if (!state.user?.orgId) throw redirect({ to: '/' });
};

// Census pages: org members with census access. A zone-only user is sent to the zone module.
const requireCensusMember = (): void => {
  requireOrgMember();
  const user = useAuthStore.getState().user;
  if (user && !hasCensus(user) && hasZone(user)) throw redirect({ to: '/zone' });
};

// Home: signed-in users only (SYS_ADMIN has no org and is handled by the Dashboard page);
// zone-only users land on the zone module.
const requireHome = (): void => {
  requireAuth();
  const user = useAuthStore.getState().user;
  if (user?.orgId && !hasCensus(user) && hasZone(user)) throw redirect({ to: '/zone' });
};

const requireZoneAccess = (): void => {
  const state = useAuthStore.getState();
  if (!state.accessToken) throw redirect({ to: '/login' });
  if (!state.user || !hasZone(state.user)) throw redirect({ to: '/' });
};

// Creating issues is for councillors only.
const requireCouncillor = (): void => {
  requireZoneAccess();
  const user = useAuthStore.getState().user;
  if (!user || !isCouncillor(user)) throw redirect({ to: '/zone' });
};

const requireOrgAdmin = (): void => {
  const state = useAuthStore.getState();
  if (!state.accessToken) throw redirect({ to: '/login' });
  if (!state.user?.isOrgAdmin) throw redirect({ to: '/' });
};

const requireSysAdmin = (): void => {
  const state = useAuthStore.getState();
  if (!state.accessToken) throw redirect({ to: '/login' });
  if (state.user?.role !== 'SYS_ADMIN') throw redirect({ to: '/' });
};

// Public routes
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: Login });
const inviteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/invite/$token', component: AcceptInvite });
const ssoHandoffRoute = createRoute({ getParentRoute: () => rootRoute, path: '/sso/handoff', component: SsoHandoff });
const forgotPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/forgot-password', component: ForgotPassword });
const resetPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/reset-password/$token', component: ResetPassword });

// Authed routes
const indexRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/',
  beforeLoad: requireHome,
  component: Dashboard,
});
const profileRoute = createRoute({ getParentRoute: () => rootRoute, path: '/profile', beforeLoad: requireAuth, component: Profile });

// Authed + orgId routes — any council member may capture field data.
const housesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/houses', beforeLoad: requireCensusMember, component: Houses });
const houseRegisterRoute = createRoute({ getParentRoute: () => rootRoute, path: '/houses/$houseId', beforeLoad: requireCensusMember, component: HouseRegister });
const personDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: '/people/$personId', beforeLoad: requireCensusMember, component: PersonDetail });
const familyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/family',
  beforeLoad: requireCensusMember,
  component: FamilyTree,
  validateSearch: (search: Record<string, unknown>): { id?: string; with?: string } => ({
    id: typeof search.id === 'string' && search.id ? search.id : undefined,
    with: typeof search.with === 'string' && search.with ? search.with : undefined,
  }),
});

// Authed + isOrgAdmin routes — dhaftharu and the user-management settings page.
const dhaftharuRoute = createRoute({ getParentRoute: () => rootRoute, path: '/dhaftharu', beforeLoad: requireOrgAdmin, component: DhaftharuList });
const dhaftharuDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: '/dhaftharu/$id', beforeLoad: requireOrgAdmin, component: DhaftharuDetail });
const settingsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings', beforeLoad: requireOrgAdmin, component: Settings });

// /dashboard is a common bookmark/typo — the home page lives at '/'. Redirect.
const dashboardRedirectRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/dashboard',
  beforeLoad: () => { throw redirect({ to: '/' }); },
});

// Surveys — list (org member), builder/results (org admin), collect (org member)
const surveysRoute = createRoute({ getParentRoute: () => rootRoute, path: '/surveys', beforeLoad: requireCensusMember, component: Surveys });
const surveyBuilderRoute = createRoute({ getParentRoute: () => rootRoute, path: '/surveys/$surveyId', beforeLoad: requireOrgAdmin, component: SurveyBuilder });
const surveyCollectRoute = createRoute({ getParentRoute: () => rootRoute, path: '/surveys/$surveyId/collect', beforeLoad: requireCensusMember, component: SurveyCollect });
const surveyResultsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/surveys/$surveyId/results',
  beforeLoad: requireOrgAdmin,
  component: SurveyResults,
  validateSearch: (search: Record<string, unknown>): { tab?: 'summary' | 'responses' } => ({
    tab: search.tab === 'responses' ? 'responses' : undefined,
  }),
});
const surveyResponseEditRoute = createRoute({ getParentRoute: () => rootRoute, path: '/surveys/$surveyId/responses/$rid/edit', beforeLoad: requireOrgAdmin, component: SurveyResponseEdit });

// Zone module — councillor issue reporting
const zoneIssuesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/zone', beforeLoad: requireZoneAccess, component: ZoneIssues });
const zoneNewIssueRoute = createRoute({ getParentRoute: () => rootRoute, path: '/zone/new', beforeLoad: requireCouncillor, component: ZoneNewIssue });
const zoneIssueDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: '/zone/issues/$id', beforeLoad: requireZoneAccess, component: ZoneIssueDetail });
const zoneDashboardRoute = createRoute({ getParentRoute: () => rootRoute, path: '/zone/dashboard', beforeLoad: requireZoneAccess, component: ZoneDashboard });
const zoneSettingsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/zone/settings', beforeLoad: requireOrgAdmin, component: ZoneSettings });

// SYS_ADMIN routes
const adminOrgsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/admin/orgs', beforeLoad: requireSysAdmin, component: AdminOrgs });
const adminOrgDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: '/admin/orgs/$orgId', beforeLoad: requireSysAdmin, component: AdminOrgDetail });

const routeTree = rootRoute.addChildren([
  loginRoute,
  ssoHandoffRoute, inviteRoute, forgotPasswordRoute, resetPasswordRoute,
  indexRoute, profileRoute,
  housesRoute, houseRegisterRoute, personDetailRoute, familyRoute,
  dhaftharuRoute, dhaftharuDetailRoute, settingsRoute,
  surveysRoute, surveyBuilderRoute, surveyCollectRoute, surveyResultsRoute, surveyResponseEditRoute,
  zoneIssuesRoute, zoneNewIssueRoute, zoneIssueDetailRoute, zoneDashboardRoute, zoneSettingsRoute,
  dashboardRedirectRoute,
  adminOrgsRoute, adminOrgDetailRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}

export function AppRouter(): JSX.Element { return <RouterProvider router={router} />; }
